/**
 * Procedural textures.
 *
 * The repository ships no binary art. Everything the arena needs — stone,
 * rune rings, banner sigils, particle sprites — is drawn once into a canvas at
 * boot and uploaded as a texture. That keeps the download tiny and sidesteps
 * asset licensing entirely, at the cost of the fidelity a real texture artist
 * would bring. Results are cached, so each texture is drawn exactly once.
 */
import * as THREE from 'three';

const cache = new Map<string, THREE.Texture>();

function make(key: string, size: number, draw: (ctx: CanvasRenderingContext2D, s: number) => void): THREE.Texture {
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  draw(ctx, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  cache.set(key, texture);
  return texture;
}

/** Cheap value noise, deterministic so reloads look identical. */
function noise2(x: number, y: number, seed = 1): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = noise2(xi, yi, seed);
  const b = noise2(xi + 1, yi, seed);
  const c = noise2(xi, yi + 1, seed);
  const d = noise2(xi + 1, yi + 1, seed);
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v;
}

function fbm(x: number, y: number, seed = 1, octaves = 4): number {
  let total = 0;
  let amp = 0.5;
  let freq = 1;
  for (let i = 0; i < octaves; i++) {
    total += smoothNoise(x * freq, y * freq, seed + i) * amp;
    freq *= 2;
    amp *= 0.5;
  }
  return total;
}

/** Dark, veined flagstone with mortar lines. */
export function stoneTexture(): THREE.Texture {
  const tex = make('stone', 512, (ctx, s) => {
    const image = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const n = fbm(x / 28, y / 28, 3, 5);
        const grain = noise2(x, y, 11) * 0.08;
        let v = 26 + n * 46 + grain * 255 * 0.25;

        // Mortar grid — 4 x 4 flagstones per tile.
        const gx = x % 128;
        const gy = y % 128;
        const edge = Math.min(gx, 128 - gx, gy, 128 - gy);
        if (edge < 3) v *= 0.45;
        else if (edge < 5) v *= 0.75;

        const i = (y * s + x) * 4;
        image.data[i] = v * 1.02;
        image.data[i + 1] = v;
        image.data[i + 2] = v * 1.12;
        image.data[i + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  });
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Roughness map for the floor: puddled, so reflections streak realistically. */
export function stoneRoughness(): THREE.Texture {
  const tex = make('stone-rough', 512, (ctx, s) => {
    const image = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const wet = fbm(x / 90, y / 90, 21, 3);
        const v = Math.max(0, Math.min(1, 0.18 + wet * 0.8)) * 255;
        const i = (y * s + x) * 4;
        image.data[i] = v;
        image.data[i + 1] = v;
        image.data[i + 2] = v;
        image.data[i + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  });
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  return tex;
}

/** Glowing concentric rune band used for magic circles and the floor sigil. */
export function runeRingTexture(seed = 1): THREE.Texture {
  return make(`rune-${seed}`, 512, (ctx, s) => {
    const c = s / 2;
    ctx.clearRect(0, 0, s, s);
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = 2;

    const rings = [0.94, 0.86, 0.62, 0.5, 0.3];
    rings.forEach((r, idx) => {
      ctx.globalAlpha = idx % 2 === 0 ? 0.95 : 0.5;
      ctx.lineWidth = idx % 2 === 0 ? 3 : 1.5;
      ctx.beginPath();
      ctx.arc(c, c, c * r, 0, Math.PI * 2);
      ctx.stroke();
    });

    // Rune glyphs around the outer band.
    const glyphs = 16;
    ctx.globalAlpha = 0.9;
    for (let i = 0; i < glyphs; i++) {
      const a = (i / glyphs) * Math.PI * 2 + seed;
      const rad = c * 0.74;
      const gx = c + Math.cos(a) * rad;
      const gy = c + Math.sin(a) * rad;
      ctx.save();
      ctx.translate(gx, gy);
      ctx.rotate(a + Math.PI / 2);
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      const kind = Math.floor(noise2(i, seed, 5) * 4);
      if (kind === 0) {
        ctx.moveTo(-6, -8);
        ctx.lineTo(6, 0);
        ctx.lineTo(-6, 8);
      } else if (kind === 1) {
        ctx.moveTo(-6, -8);
        ctx.lineTo(-6, 8);
        ctx.moveTo(-6, 0);
        ctx.lineTo(6, 0);
        ctx.moveTo(6, -6);
        ctx.lineTo(6, 6);
      } else if (kind === 2) {
        ctx.arc(0, 0, 6, 0.6, Math.PI * 1.7);
        ctx.moveTo(0, -8);
        ctx.lineTo(0, 8);
      } else {
        ctx.moveTo(-7, 7);
        ctx.lineTo(0, -8);
        ctx.lineTo(7, 7);
        ctx.closePath();
      }
      ctx.stroke();
      ctx.restore();
    }

    // Radial spokes tying the bands together.
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * c * 0.5, c + Math.sin(a) * c * 0.5);
      ctx.lineTo(c + Math.cos(a) * c * 0.86, c + Math.sin(a) * c * 0.86);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  });
}

/** Soft radial falloff used by every additive sprite. */
export function glowTexture(): THREE.Texture {
  return make('glow', 128, (ctx, s) => {
    const c = s / 2;
    const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    grad.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);
  });
}

/** House banner: a deep field with a four-pointed star sigil. */
export function bannerTexture(): THREE.Texture {
  return make('banner', 256, (ctx, s) => {
    const grad = ctx.createLinearGradient(0, 0, 0, s);
    grad.addColorStop(0, '#131c3a');
    grad.addColorStop(0.5, '#1b2a55');
    grad.addColorStop(1, '#0d1428');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);

    ctx.strokeStyle = 'rgba(198,170,96,0.85)';
    ctx.lineWidth = 4;
    ctx.strokeRect(14, 14, s - 28, s - 28);

    const c = s / 2;
    ctx.fillStyle = 'rgba(214,190,120,0.92)';
    for (const scale of [1, 0.5]) {
      ctx.beginPath();
      ctx.moveTo(c, c - 62 * scale);
      ctx.quadraticCurveTo(c + 12 * scale, c - 12 * scale, c + 62 * scale, c);
      ctx.quadraticCurveTo(c + 12 * scale, c + 12 * scale, c, c + 62 * scale);
      ctx.quadraticCurveTo(c - 12 * scale, c + 12 * scale, c - 62 * scale, c);
      ctx.quadraticCurveTo(c - 12 * scale, c - 12 * scale, c, c - 62 * scale);
      ctx.fill();
    }
  });
}

/** Faint noise sheet used to break up flat wall shading. */
export function grungeTexture(): THREE.Texture {
  const tex = make('grunge', 256, (ctx, s) => {
    const image = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const v = (0.45 + fbm(x / 40, y / 40, 7, 4) * 0.6) * 255;
        const i = (y * s + x) * 4;
        image.data[i] = v * 0.95;
        image.data[i + 1] = v * 0.94;
        image.data[i + 2] = v;
        image.data[i + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  });
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

export function disposeTextures() {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}
